import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Modal, Input, Field, Message, Header, Toast, Groupper, Toolbar, Label, Icon, Checkbox } from "react-frontier";
import { useBreadcrumbs, useUser } from "../hooks";
import type { Employment as EmploymentType, EmploymentSchedule, EmploymentScheduleDetail, SetLoading } from "../SiteClasses";
import { addCommas, bindClick, bindFormChange } from "../SiteUtil";
import { Dropdown } from "semantic-ui-react";
import { useParams, useLocation } from "react-router-dom";
import { UserLog, Autocomplete, ScheduleView, EmploymentWorkers, LocationMap, getEmploymentStatus } from "../components";
import UserAccess from "../UserAccess";
import Validator from "../Validator";
import moment from "moment";
import API from "../API";

const PAGE_SIZE = 20;

interface ScheduleForm{
	days: number[];
	time_start: number;
	time_end: number;
	salary: string;
}

interface EmploymentForm{
	employment_name: string;
	date_start: number;
	date_end: number;
	location: any;
	late_seconds: number;
	penalty_seconds: number;
	allow_remote: boolean;
	geofence_radius: number;
}

enum Modals{
	SCHEDULE_FORM = 1,
	DELETE_SCHEDULE = 2,
	EDIT_EMPLOYMENT = 3,
	DELETE_EMPLOYMENT = 4,
	SCHEDULE_DETAIL = 7,
}

enum RightControl{
	SCHEDULES = 2,
	WORKERS = 3,
}

const SidebarControl = {
	'schedules': RightControl.SCHEDULES,
	'workers': RightControl.WORKERS,
}

var DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

var TIME_OPTIONS = Array.from({ length: 49 }, (_, i) => {
	var minutes = i * 30;
	return {
		text: moment().startOf('day').add(minutes, 'minutes').format('HH:mm'),
		value: minutes,
		key: minutes
	};
});

var formatConflictPrompts = (res: { message: string, data?: { conflicts?: { name: string, employment_name: string }[] } })=>{
	var conflicts = res.data?.conflicts || [];
	return [res.message, ...conflicts.map(c => `${c.name} — ${c.employment_name}`)];
};

var Employment = ()=>{
	var params = useParams<{ id: string }>();
	var location = useLocation();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess, user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [employmentData, setEmploymentData] = useState<EmploymentType>(null);
	var [schedules, setSchedules] = useState<EmploymentSchedule[]>(null);
	var [shownModal, setShownModal] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [selectedSchedule, setSelectedSchedule] = useState<EmploymentSchedule>(null);
	var [selectedScheduleDetail, setSelectedScheduleDetail] = useState<EmploymentScheduleDetail>(null);
	var [loadingScheduleDetail, setLoadingScheduleDetail] = useState<boolean>(false);
	var [isEditMode, setIsEditMode] = useState<boolean>(false);
	var [rightControl, setRightControl] = useState<RightControl>(RightControl.SCHEDULES);
	var [scheduleForm, setScheduleForm] = useState<ScheduleForm>({
		days: [1],
		time_start: 480,
		time_end: 1020,
		salary: '',
	});
	var [employmentForm, setEmploymentForm] = useState<EmploymentForm>({
		employment_name: '',
		date_start: moment().startOf('day').unix(),
		date_end: moment().endOf('day').unix(),
		location: null,
		late_seconds: 15,
		penalty_seconds: 30,
		allow_remote: false,
		geofence_radius: 500,
	});

	useEffect(()=>{
		setTitle('Empleo #'+parseInt(params.id));
		get();
	}, []);

	useEffect(()=>{
		if(employmentData){
			var reg_res = (/\/employments\/[0-9]+\/(?<route>.*)/gi).exec(location.pathname);
			if(!!reg_res && reg_res.groups.route){
				onRouteChanged(reg_res.groups.route as any)
			}
		}
	}, [location?.pathname]);

	var onRouteChanged = (path: keyof (typeof SidebarControl))=>{
		setRightControl(SidebarControl[path] || RightControl.SCHEDULES);
	}

	var get = ()=>{
		API.getEmployment(parseInt(params.id)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setEmploymentData(res.data);
			setSchedules(res.data.schedules);
		}).catch(()=> setLoadError('Hubo un error inesperado al cargar el empleo'));
	}

	var onShowSchedule = (schedule: EmploymentSchedule)=>{
		setSelectedSchedule(schedule);
		setLoadingScheduleDetail(true);
		setShownModal(Modals.SCHEDULE_DETAIL);
		API.getEmploymentSchedule(schedule.schedule_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setSelectedScheduleDetail(res.data);
		}).catch(()=>{
			Toast.error('Hubo un error cargando el horario');
		}).finally(()=>{
			setLoadingScheduleDetail(false);
		});
	}

	var showScheduleModal = (schedule?: EmploymentSchedule)=>{
		setIsEditMode(!!schedule);
		setSelectedSchedule(schedule || null);
		if(schedule){
			setScheduleForm({
				days: [schedule.day],
				time_start: schedule.time_start,
				time_end: schedule.time_end,
				salary: String(schedule.salary),
			});
		}else{
			setScheduleForm({
				days: [1],
				time_start: 480,
				time_end: 1020,
				salary: '',
			});
		}
		setModalPrompts([]);
		setShownModal(Modals.SCHEDULE_FORM);
	}

	var showDeleteScheduleModal = ()=>{
		setModalPrompts([]);
		setShownModal(Modals.DELETE_SCHEDULE);
	}

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(scheduleForm, {
			days: [{ rule: (val: number[]) => val && val.length > 0, prompt: 'Selecciona al menos un día.' }],
			time_start: [{ rule: 'number', label: 'Hora de inicio' }],
			time_end: [{ rule: 'number', label: 'Hora de fin' }],
			salary: [{ rule: 'number', label: 'Salario' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;
		if(scheduleForm.time_start >= scheduleForm.time_end) return setModalPrompts(['La hora de inicio debe ser menor a la hora de fin']);

		setLoading(true);
		var salary = parseFloat(scheduleForm.salary);

		if(!isEditMode){
			Promise.all(scheduleForm.days.map(day =>
				API.createEmploymentSchedule(employmentData.employment_id, day, scheduleForm.time_start, scheduleForm.time_end, salary)
			)).then(results =>{
				var errors = results.filter(r => r.error);
				if(errors.length > 0) return setModalPrompts([errors[0].message]);
				Toast.success(results.length === 1 ? 'Horario creado' : `${results.length} horarios creados`);
				var newSchedules = results.map((res, i) => ({
					schedule_id: res.data.schedule_id,
					employment_id: employmentData.employment_id,
					day: scheduleForm.days[i],
					time_start: scheduleForm.time_start,
					time_end: scheduleForm.time_end,
					salary,
					deleted: false,
					user_creator: user.user_id,
					user_creator_name: `${user.first_name} ${user.last_name}`,
					date_created: moment().unix(),
				}));
				setSchedules([...schedules, ...newSchedules]);
				setShownModal(null);
			}).catch(()=>{
				setModalPrompts(['Hubo un error inesperado creando el horario']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.editEmploymentSchedule(selectedSchedule.schedule_id, scheduleForm.days[0], scheduleForm.time_start, scheduleForm.time_end, salary).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Horario actualizado');
				setSchedules(schedules.map(s =>
					s.schedule_id === selectedSchedule.schedule_id
						? { ...s, day: scheduleForm.days[0], time_start: scheduleForm.time_start, time_end: scheduleForm.time_end, salary }
						: s
				));
				if(selectedScheduleDetail && selectedScheduleDetail.schedule_id === selectedSchedule.schedule_id){
					setSelectedScheduleDetail({
						...selectedScheduleDetail,
						day: scheduleForm.days[0],
						time_start: scheduleForm.time_start,
						time_end: scheduleForm.time_end,
						salary,
					});
				}
				setShownModal(null);
			}).catch(()=>{
				setModalPrompts(['Hubo un error inesperado actualizando el horario']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var deleteSchedule = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteEmploymentSchedule(selectedSchedule.schedule_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Horario eliminado');
			setSchedules(schedules.filter(s => s.schedule_id !== selectedSchedule.schedule_id));
			if(selectedScheduleDetail && selectedScheduleDetail.schedule_id === selectedSchedule.schedule_id){
				setSelectedScheduleDetail(null);
			}
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado eliminando el horario']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var showEditEmploymentModal =()=>{
		setEmploymentForm({
			employment_name: employmentData.employment_name,
			date_start: employmentData.date_start || moment().startOf('day').unix(),
			date_end: employmentData.date_end || moment().endOf('day').unix(),
			location: employmentData.location,
			late_seconds: employmentData.late_seconds / 60,
			penalty_seconds: employmentData.penalty_seconds / 60,
			allow_remote: employmentData.allow_remote,
			geofence_radius: employmentData.geofence_radius,
		});
		setModalPrompts([]);
		setShownModal(Modals.EDIT_EMPLOYMENT);
	}

	var showDeleteEmploymentModal = ()=>{
		setModalPrompts([]);
		setShownModal(Modals.DELETE_EMPLOYMENT);
	}

	var editEmployment = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(employmentForm, {
			employment_name: [{ rule: 'minLength', params: [3], label: 'Nombre de empleo' }],
			date_start: [{ rule: 'number', label: 'Fecha de inicio' }],
			date_end: [{ rule: 'number', label: 'Fecha de fin' }],
			late_seconds: [{ rule: 'number', label: 'Tolerancia' }],
			penalty_seconds: [{ rule: 'number', label: 'Retardo' }],
			geofence_radius: [{ rule: 'number', label: 'Radio de registro' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		var ds = moment.unix(employmentForm.date_start).startOf('day').unix();
		var de = moment.unix(employmentForm.date_end).endOf('day').unix();
		if(ds >= de) return setModalPrompts(['La fecha de inicio debe ser menor a la fecha de fin']);

		setLoading(true);
		API.editEmployment(employmentData.employment_id, employmentForm.employment_name, ds, de, employmentForm.location, employmentForm.allow_remote, employmentForm.geofence_radius, employmentForm.late_seconds * 60, employmentForm.penalty_seconds * 60).then(res=>{
			if(res.error) return setModalPrompts(formatConflictPrompts(res));
			Toast.success('Empleo actualizado');
			setEmploymentData({
				...employmentData,
				employment_name: employmentForm.employment_name,
				date_start: ds,
				date_end: de,
				location: employmentForm.location,
				allow_remote: employmentForm.allow_remote,
				geofence_radius: employmentForm.geofence_radius,
				late_seconds: employmentForm.late_seconds * 60,
				penalty_seconds: employmentForm.penalty_seconds * 60,
			});
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado actualizando el empleo']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onEmploymentDeletedChange = (setLoading: SetLoading)=>{
		var deleted = !employmentData.deleted;
		setLoading(true);
		API.deleteEmployment(employmentData.employment_id, deleted).then(res=>{
			if(res.error) return setModalPrompts(formatConflictPrompts(res));
			Toast.success(deleted ? 'Empleo deshabilitado' : 'Empleo habilitado');
			setEmploymentData({ ...employmentData, deleted });
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts([`Hubo un error inesperado ${deleted ? 'deshabilitando' : 'habilitando'} el empleo`]);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onFormChange = bindFormChange(scheduleForm, setScheduleForm);
	var onEmploymentFormChange = bindFormChange(employmentForm, setEmploymentForm);

	var control_header = useMemo(()=>{
		switch(rightControl){
			case RightControl.SCHEDULES: return 'Horarios';
			case RightControl.WORKERS: return 'Empleados';
		}
	}, [rightControl]);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!employmentData) return <Header text='Cargando datos' loading />
	var employmentStatus = getEmploymentStatus(employmentData);
	var geofenceRadius = Number(employmentForm.geofence_radius);
	var mapRadius = Number.isFinite(geofenceRadius) && geofenceRadius > 0 ? geofenceRadius : null;

	return <div className='ar employment-view-screen'>
		<div className="left">
			<Groupper width={500} fitted title={'Información del Empleo'} style={{ marginTop: 0, marginBottom: 0 }}>
				<Toolbar stretch>
					{hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS) && !employmentData.deleted && (
						<Toolbar.Item iconName='edit' text='Editar' onClick={showEditEmploymentModal} />
					)}
					{hasAccess(UserAccess.EMPLOYMENTS.DELETE_EMPLOYMENTS) && (
						<Toolbar.Item
							iconName={employmentData.deleted ? 'unlock' : 'ban'}
							text={employmentData.deleted ? 'Habilitar' : 'Deshabilitar'}
							onClick={showDeleteEmploymentModal}
						/>
					)}
				</Toolbar>
				<Table
					details
					striped
					fitted
					data={[
						['ID', employmentData.employment_id],
						['Nombre', employmentData.employment_name],
						['Estatus', <Label color={employmentStatus.color}>{employmentStatus.text}</Label>],
						[],
						['Fecha de inicio', moment.unix(employmentData.date_start).format('DD/MM/YYYY')],
						['Fecha de fin', employmentData.date_end ? moment.unix(employmentData.date_end).format('DD/MM/YYYY') : '-'],
						['Ubicación', employmentData.location ? employmentData.location.name : '-'],
						['Remoto', employmentData.allow_remote ? <Icon name='check' color='green' /> : <Icon name='times' color='red' />],
						['Radio de registro', addCommas(employmentData.geofence_radius, false) + ' mts'],
						['Tolerancia', moment.unix(employmentData.late_seconds).utc().format('mm') + ' min'],
						['Retardo', moment.unix(employmentData.penalty_seconds).utc().format('mm') + ' min'],
						['Empleados', employmentData.employee_count],
					]}
				/>
				<UserLog user={employmentData} fitted style={{ margin: 0, maxWidth: 500 }}/>
			</Groupper>
			{employmentData.location && (
				<Groupper style={{ marginTop: 0, padding: 0 }}>
					<LocationMap
						location={employmentData.location}
						radius={employmentData.geofence_radius}
					/>
				</Groupper>
			)}
		</div>
		<div className="right">
			<div className="header">
				{control_header}
			</div>
			<div className="content">
				{rightControl===RightControl.SCHEDULES ? (
					!schedules ? (
						<Header loading text='Cargando horarios' />
					) : (
						<ScheduleView
							title="Horarios"
							actions={
								hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS) && !employmentData.deleted && (
									<Button text="Agregar" iconName="plus-circle" color="black" size="tiny" onClick={bindClick(showScheduleModal)} />
								)
							}
							schedule={DAYS.map((dayName, dayIndex) => {
								var daySchedules = schedules.filter(s => s.day === dayIndex);
								return {
									text: dayName,
									times: daySchedules.map(schedule => ({
										start: Math.floor(schedule.time_start / 60),
										end: Math.floor(schedule.time_end / 60),
										text: `${moment().startOf('day').add(schedule.time_start, 'minutes').format('HH:mm')} - ${moment().startOf('day').add(schedule.time_end, 'minutes').format('HH:mm')} ($${addCommas(schedule.salary)})`,
										onClick: employmentData.deleted ? null : () => onShowSchedule(schedule)
									}))
								};
							})}
						/>
					)
				) : rightControl===RightControl.WORKERS ? (
					<EmploymentWorkers employment_id={employmentData.employment_id} readonly={employmentData.deleted} />
				) : null}
			</div>
		</div>
		<Modal open={shownModal === Modals.SCHEDULE_FORM} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>{isEditMode ? 'Editar horario' : 'Crear horario'}</Modal.Header>
			<Modal.Content>
				<Field label={isEditMode ? 'Día' : 'Días'}>
					<Dropdown
						placeholder={isEditMode ? 'Selecciona un día' : 'Selecciona uno o más días'}
						selection
						multiple={!isEditMode}
						options={DAYS.map((day, idx) => ({ text: day, value: idx }))}
						onChange={(e, { value }) => setScheduleForm(f => ({ ...f, days: isEditMode ? [value as number] : (value as number[]) }))}
						value={isEditMode ? scheduleForm.days[0] : scheduleForm.days}
					/>
				</Field>
				<Field amount={2}>
					<Field label="Hora inicio">
						<Dropdown
							placeholder='Selecciona hora de inicio'
							selection
							scrolling
							options={TIME_OPTIONS}
							onChange={onFormChange('time_start', true)}
							value={scheduleForm.time_start}
						/>
					</Field>
					<Field label="Hora fin">
						<Dropdown
							placeholder='Selecciona hora de fin'
							selection
							scrolling
							options={TIME_OPTIONS}
							onChange={onFormChange('time_end', true)}
							value={scheduleForm.time_end}
						/>
					</Field>
				</Field>
				<Field label='Salario'>
					<Input
						placeholder='Salario'
						value={scheduleForm.salary}
						onChange={onFormChange('salary')}
					/>
				</Field>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal, isEditMode ?  Modals.SCHEDULE_DETAIL : null)} />
				<Button text={isEditMode ? 'Guardar' : 'Crear'} color='black' onClick={submit} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.DELETE_SCHEDULE} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>Eliminar horario</Modal.Header>
			<Modal.Content>
				<Header text='¿Estás seguro de que deseas eliminar este horario?' subtext='No podrá ser asignado a más empleados.' />
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal, Modals.SCHEDULE_DETAIL)} />
				<Button text='Eliminar' color='red' onClick={deleteSchedule} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT_EMPLOYMENT} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>Editar empleo</Modal.Header>
			<Modal.Content>
				<Input
					label="Nombre"
					value={employmentForm.employment_name}
					onChange={onEmploymentFormChange('employment_name')}
				/>
				<Field amount={2}>
					<Input
						label='Fecha de inicio'
						value={employmentForm.date_start}
						onChange={onEmploymentFormChange('date_start')}
						calendar={{
							date: employmentForm.date_start,
							mode: 'date',
							format: 'DD/MM/YYYY'
						}}
						style={{ width: '100%'}}
					/>
					<Input
						label='Fecha de fin'
						value={employmentForm.date_end}
						onChange={onEmploymentFormChange('date_end')}
						calendar={{
							date: employmentForm.date_end,
							mode: 'date',
							format: 'DD/MM/YYYY'
						}}
						style={{ width: '100%'}}
					/>
				</Field>
				<Field amount={2}>
					<Input label="Tolerancia" value={employmentForm.late_seconds} onChange={onEmploymentFormChange('late_seconds')} />
					<Input label="Retardo" value={employmentForm.penalty_seconds} onChange={onEmploymentFormChange('penalty_seconds')} />
				</Field>
				<Input label="Radio de registro (mts)" value={employmentForm.geofence_radius} onChange={onEmploymentFormChange('geofence_radius')} />
				<Field label="Ubicación">
					<Autocomplete
						placeholder={employmentForm.location ? employmentForm.location.name : "Ubicación"}
						options={{ types: [] }}
						onPlaceSelected={onEmploymentFormChange('location')}
					/>
				</Field>
				{employmentForm.location && (
					<LocationMap
						location={employmentForm.location}
						radius={mapRadius}
						height={200}
					/>
				)}
				<Checkbox label="Permitir trabajo remoto" checked={employmentForm.allow_remote} onChange={onEmploymentFormChange('allow_remote')} style={{ marginTop: 20 }}/>
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' onClick={editEmployment} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.DELETE_EMPLOYMENT} onClose={bindClick(setShownModal, null)} size='tiny'>
			<Modal.Header>{employmentData.deleted ? 'Habilitar empleo' : 'Deshabilitar empleo'}</Modal.Header>
			<Modal.Content>
				<Header
					text={employmentData.deleted ? '¿Deseas habilitar este empleo?' : '¿Estás seguro de que deseas deshabilitar este empleo?'}
					subtext={employmentData.deleted ? 'El empleo volverá a estar disponible en el sistema.' : 'El empleo dejará de estar disponible para asignar a empleados.'}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' onClick={bindClick(setShownModal, null)} />
				<Button
					text={employmentData.deleted ? 'Habilitar' : 'Deshabilitar'}
					color={employmentData.deleted ? 'green' : 'red'}
					onClick={onEmploymentDeletedChange}
				/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.SCHEDULE_DETAIL} onClose={bindClick(setShownModal, null)} size='mini'>
			<Modal.Header>Información de horario</Modal.Header>
			<Modal.Content style={{ padding: 0 }}>
				{loadingScheduleDetail ? (
					<Header loading text='Cargando datos de horario' />
				) : selectedScheduleDetail ? (
					<>
						<Toolbar fitted stretch style={{ borderRadius: 0}}>
							<Toolbar.Item text="Editar" iconName='pen' onClick={() => showScheduleModal(selectedScheduleDetail)}/>
							{hasAccess(UserAccess.EMPLOYMENTS.DELETE_EMPLOYMENTS) && <Toolbar.Item text="Eliminar" iconName='close' onClick={() => showDeleteScheduleModal()}/>}
						</Toolbar>
						<Table
							details
							striped
							data={[
								['Día', DAYS[selectedScheduleDetail.day]],
								['Horario', `${moment().startOf('day').add(selectedScheduleDetail.time_start, 'minutes').format('HH:mm')} - ${moment().startOf('day').add(selectedScheduleDetail.time_end, 'minutes').format('HH:mm')}`],
								['Salario', `$${addCommas(selectedScheduleDetail.salary)}`],
								['Trabajadores', selectedScheduleDetail.employees],
								['Creado por', selectedScheduleDetail.user_creator_name],
								['Fecha de creación', moment.unix(selectedScheduleDetail.date_created).format('DD/MM/YYYY HH:mm')],
							]}
						/>
					</>
				) : null}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal, null)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default Employment;
